import { cn } from "@heroui/react";
import { LazyMotion, domAnimation, useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import ConversationPanel from "./components/ConversationPanel";
import ConversationView from "./components/ConversationView";
import ExecutionTraceWindow from "./components/ExecutionTraceWindow";
import GlobalSidebar from "./components/GlobalSidebar";
import GroupPanel from "./components/GroupPanel";
import InboxShell from "./components/InboxShell";
import TopNavigation from "./components/TopNavigation";
import { useInboxDemoController } from "./controller/useInboxDemoController";
import { defaultDemo } from "./data/defaultDemo";
import type { InboxInteractiveDemoProps } from "./data/types";
import { useInboxDemoPlayback } from "./hooks/useInboxDemoPlayback";
import { useInboxDemoState } from "./hooks/useInboxDemoState";
import { defaultInboxDemoIcons } from "./icons/defaultIcons";
import "./styles/inbox-demo.css";

type FrontWindow = "inbox" | "execution";

export default function InboxInteractiveDemo({
  data = defaultDemo,
  icons,
  initialConversationId,
  demoSequence,
  autoPlayFirstConversation = false,
  showGroupPanel = false,
  onSendMessage,
  className,
}: InboxInteractiveDemoProps) {
  const mergedIcons = useMemo(
    () => ({ ...defaultInboxDemoIcons, ...icons }),
    [icons],
  );
  const reducedMotion = Boolean(useReducedMotion());
  const [frontWindow, setFrontWindow] = useState<FrontWindow>("execution");
  const [mobilePane, setMobilePane] = useState<"list" | "detail">("detail");

  const state = useInboxDemoState({
    data,
    fallbackData: defaultDemo,
    initialConversationId,
    onSendMessage,
  });

  const firstConversation = state.conversations[0];
  const playback = useInboxDemoPlayback({
    conversation: firstConversation,
    enabled: autoPlayFirstConversation,
    reducedMotion,
    selectedConversationId: state.selectedConversationId,
  });

  const controller = useInboxDemoController({
    sequence: demoSequence,
    actions: {
      selectConversation: state.selectConversation,
      setDraft: state.setDraft,
      sendMessage: state.sendMessage,
      scrollMessages: state.scrollMessages,
    },
  });

  const stopAuto = useCallback(() => {
    playback.stop();
    controller.handleUserIntent();
  }, [controller, playback.stop]);

  const showExecutionWindow =
    state.selectedConversationId === firstConversation?.id;

  useEffect(() => {
    if (!showExecutionWindow) {
      setFrontWindow("inbox");
      return;
    }
    if (playback.execution.isRunning) {
      setFrontWindow("execution");
    }
  }, [playback.execution.isRunning, showExecutionWindow]);

  const displayedConversation = useMemo(() => {
    const conversation = state.selectedConversation;
    if (!conversation) return undefined;
    if (
      !autoPlayFirstConversation ||
      conversation.id !== firstConversation?.id ||
      !playback.isPlaying
    ) {
      return conversation;
    }
    return {
      ...conversation,
      messages: conversation.messages.slice(0, playback.revealedCount),
    };
  }, [
    autoPlayFirstConversation,
    firstConversation?.id,
    playback.isPlaying,
    playback.revealedCount,
    state.selectedConversation,
  ]);

  return (
    <LazyMotion features={domAnimation}>
      <div
        className={cn(
          "relative isolate h-full min-h-0 w-full min-w-0 overflow-visible",
          className,
        )}
      >
        <div
          className="inbox-interactive-demo light absolute inset-0 flex min-h-0 min-w-0 overflow-hidden rounded-[16px] text-foreground"
          data-theme="light"
          style={{ zIndex: frontWindow === "inbox" ? 20 : 10 }}
          onPointerDown={() => setFrontWindow("inbox")}
        >
          <InboxShell
            navigation={
              <TopNavigation icons={mergedIcons} navigation={state.navigation} />
            }
            sidebar={<GlobalSidebar icons={mergedIcons} />}
            workspace={
              <>
                <div
                  className={cn(
                    "h-full min-w-0 overflow-hidden rounded-[12px] bg-[var(--inbox-demo-panel-bg)]",
                    mobilePane === "list" ? "flex min-h-0 w-full flex-1" : "hidden",
                    "md:flex md:w-auto md:shrink-0 md:flex-none",
                    showGroupPanel ? "md:w-[440px]" : "md:w-[280px]",
                  )}
                >
                  {showGroupPanel ? (
                    <GroupPanel
                      conversations={state.conversations}
                      expandedGroupIds={state.expandedGroupIds}
                      groups={state.groups}
                      icons={mergedIcons}
                      selectedGroupId={state.selectedGroupId}
                      onSelect={(groupId) => {
                        stopAuto();
                        state.selectGroup(groupId);
                      }}
                      onToggle={state.toggleGroup}
                    />
                  ) : null}
                  <ConversationPanel
                    conversations={state.visibleConversations}
                    icons={mergedIcons}
                    listRef={state.conversationListRef}
                    selectedConversationId={state.selectedConversationId}
                    title={state.selectedGroupLabel}
                    onSelect={(conversationId) => {
                      stopAuto();
                      state.selectConversation(conversationId);
                      setMobilePane("detail");
                    }}
                    onUserIntent={stopAuto}
                  />
                </div>
                <ConversationView
                  className={cn(
                    mobilePane === "detail" ? "flex" : "hidden",
                    "md:flex",
                  )}
                  conversation={displayedConversation}
                  draft={state.draft}
                  emptyStateKey={state.selectedGroupId}
                  icons={mergedIcons}
                  messageListRef={state.messageListRef}
                  navigation={state.navigation}
                  onBack={() => {
                    stopAuto();
                    setMobilePane("list");
                  }}
                  onDraftChange={(value) => {
                    stopAuto();
                    state.setDraft(value);
                  }}
                  onSend={() => {
                    stopAuto();
                    void state.sendMessage();
                  }}
                  onUserIntent={stopAuto}
                />
              </>
            }
          />
        </div>

        {showExecutionWindow ? (
          <div
            className={cn(
              "absolute bg-transparent",
              mobilePane === "list" && "max-md:hidden",
            )}
            style={{
              zIndex: frontWindow === "execution" ? 30 : 10,
              right: -32,
              bottom: -16,
              width: 340,
              maxWidth: "calc(100% - 16px)",
            }}
            onPointerDown={(event) => {
              event.stopPropagation();
              setFrontWindow("execution");
            }}
          >
            <ExecutionTraceWindow
              isRunning={playback.execution.isRunning}
              items={playback.execution.items}
            />
          </div>
        ) : null}
      </div>
    </LazyMotion>
  );
}
